import { rewriteHtmlImageSrc } from '@/common/utils/rewriteHtmlImageSrc'

export const MHTML_WIDGET_RESIZE_TYPE = 'mhtml-resize'

export function htmlFragmentContainsScript(html: string): boolean {
  return /<script[\s>]/i.test(html)
}

export function unescapeHtmlEntitiesIfNeeded(html: string): string {
  if (!html.includes('&lt;') && !html.includes('&gt;')) return html
  return html
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
}

export function isMhtmlPreviewNavigableHref(href: string | null | undefined): boolean {
  const trimmed = String(href ?? '').trim()
  if (!trimmed) return false
  if (trimmed === '#' || trimmed.startsWith('#')) return false
  if (/^javascript:/i.test(trimmed)) return false
  return true
}

export type MhtmlWidgetResizeMessage = {
  type: typeof MHTML_WIDGET_RESIZE_TYPE
  id: string
  h: number
  overlay: boolean
}

export function parseMhtmlWidgetResizeMessage(
  data: unknown,
): MhtmlWidgetResizeMessage | null {
  if (!data || typeof data !== 'object') return null
  const rec = data as Record<string, unknown>
  if (rec.type !== MHTML_WIDGET_RESIZE_TYPE) return null
  const id = String(rec.id ?? '').trim()
  const h = Number(rec.h)
  if (!id || !Number.isFinite(h) || h < 0) return null
  return {
    type: MHTML_WIDGET_RESIZE_TYPE,
    id,
    h,
    overlay: rec.overlay === true,
  }
}

/**
 * Overlay uses a viewport-sized iframe; `document.scrollHeight` then stays ≥ viewport
 * after close. Keep the last in-flow height until a non-overlay measure arrives.
 */
export function applyMhtmlWidgetResize(
  current: { height: number; overlay: boolean },
  next: MhtmlWidgetResizeMessage,
): { height: number; overlay: boolean } {
  if (next.overlay) {
    return { height: current.height, overlay: true }
  }
  return {
    height: next.h > 0 ? next.h : current.height,
    overlay: false,
  }
}

function escapeJsString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")
}

function buildResizeShim(frameId: string): string {
  const id = escapeJsString(frameId)
  return `<script>
(function () {
  var frameId = '${id}';
  function isOverlayOpen() {
    var nodes = document.querySelectorAll('[role="dialog"], .pdcb-modal');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.hasAttribute('hidden')) continue;
      var st = window.getComputedStyle(el);
      if (st.display === 'none' || st.visibility === 'hidden') continue;
      if (st.position === 'fixed') return true;
    }
    return false;
  }
  function contentBottom() {
    var bottom = 0;
    var kids = document.body ? document.body.children : [];
    var scrollY = window.scrollY || window.pageYOffset || 0;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      var tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK') continue;
      if (el.hasAttribute('hidden')) continue;
      var st = window.getComputedStyle(el);
      if (!st || st.display === 'none' || st.visibility === 'hidden' || st.position === 'fixed') continue;
      var rect = el.getBoundingClientRect();
      bottom = Math.max(bottom, rect.bottom + scrollY);
    }
    return Math.ceil(bottom);
  }
  function send() {
    var overlay = isOverlayOpen();
    var h = overlay ? (window.innerHeight || 0) : contentBottom();
    parent.postMessage({ type: '${MHTML_WIDGET_RESIZE_TYPE}', id: frameId, h: h, overlay: overlay }, '*');
  }
  send();
  window.addEventListener('load', send);
  window.addEventListener('resize', send);
  document.addEventListener('click', function () { setTimeout(send, 0); }, true);
  document.addEventListener('keydown', function () { setTimeout(send, 0); }, true);
  if (window.MutationObserver) {
    new MutationObserver(send).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'style', 'class']
    });
  }
  if (window.ResizeObserver && document.documentElement) {
    new ResizeObserver(send).observe(document.documentElement);
  }
})();
` + '<' + '/script>'
}

/** Isolated widget document for `sandbox="allow-scripts"` iframe (no same-origin). */
export function buildMhtmlWidgetSrcdoc(fragment: string, frameId: string): string {
  const body = rewriteHtmlImageSrc(fragment ?? '')
  const safeId = String(frameId ?? '').replace(/[^a-zA-Z0-9_-]/g, '') || 'mhtml'
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;min-height:0;height:auto;}</style></head><body>${body}${buildResizeShim(safeId)}</body></html>`
}
